import { formatPhone } from '@fundline/shared/phone';
import { Link } from '@tanstack/react-router';
import { Check, Copy, X } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Avatar } from '../../components/ui/Avatar.tsx';
import { Spinner } from '../../components/ui/Layout.tsx';
import { TagChip } from '../../components/ui/TagChip.tsx';
import { useWorkspaceClaims } from '../../lib/auth.tsx';
import { useMerchantLocalTime } from '../compliance/SendingWindow.tsx';
import { useMembersQuery } from '../members/use-members.ts';
import { useMerchantQuery } from '../merchants/use-merchants.ts';
import { useTagsQuery } from '../settings/use-tags.ts';

function PanelCard({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="rounded-lg bg-surface-raised p-4 ring-1 ring-hairline">
      {title ? <h3 className="footnote mb-2 font-semibold text-ink-muted">{title}</h3> : null}
      {children}
    </section>
  );
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="fl-icon-btn text-ink-muted hover:text-ink focus-visible:outline-2 focus-visible:outline-focus-ring"
      aria-label={copied ? `${label} copied` : `Copy ${label.toLowerCase()}`}
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => setCopied(true));
      }}
    >
      {copied ? (
        <Check size={18} strokeWidth={1.75} aria-hidden />
      ) : (
        <Copy size={18} strokeWidth={1.75} aria-hidden />
      )}
    </button>
  );
}

/**
 * The details panel (brand/layout.md): merchant, contact, texting status and tags. Deal, sequence and AI
 * auto-reply join with M9–M11. Empty fields are hidden, not shown blank. A column on wide screens, an
 * overlay on narrower desktops, a bottom sheet on phones.
 */
export function DetailsPanel({
  merchantId,
  mode,
  onClose,
}: {
  merchantId: string;
  mode: 'column' | 'overlay' | 'sheet';
  onClose: () => void;
}) {
  const { workspaceId } = useWorkspaceClaims();
  const merchant = useMerchantQuery(workspaceId, merchantId);
  const members = useMembersQuery(workspaceId);
  const tags = useTagsQuery(workspaceId);
  const closeButton = useRef<HTMLButtonElement>(null);
  const floating = mode !== 'column';

  useEffect(() => {
    if (!floating) return;
    closeButton.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [floating, onClose]);

  const m = merchant.data;
  const { local } = useMerchantLocalTime(m ?? null);
  const rep = m ? members.data?.find((x) => x.userId === m.assignedRepId) : undefined;
  const tagList = m ? (tags.data ?? []).filter((t) => m.tagIds.includes(t.id)) : [];

  const body = merchant.isPending ? (
    <Spinner label="Loading details" />
  ) : merchant.isError ? (
    <Alert tone="error">{merchant.error.message}</Alert>
  ) : !m ? null : (
    <>
      <PanelCard>
        <div className="flex flex-col items-center gap-2 text-center">
          <span className="[&>.fl-avatar]:size-16">
            <Avatar name={m.businessName ?? m.ownerName} brand />
          </span>
          <p className="headline text-ink">
            {m.businessName ?? m.ownerName ?? formatPhone(m.phone)}
          </p>
          {m.businessName && m.ownerName ? (
            <p className="callout text-ink-muted">{m.ownerName}</p>
          ) : null}
          <Link
            to="/merchants/$merchantId"
            params={{ merchantId: m.id }}
            className="fl-btn fl-btn--secondary fl-btn--sm mt-1 focus-visible:outline-2 focus-visible:outline-focus-ring"
          >
            Open merchant
          </Link>
        </div>
      </PanelCard>

      <PanelCard title="Contact">
        <dl className="callout grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1">
          <dt className="text-ink-muted">Mobile</dt>
          <dd className="flex items-center justify-between gap-1 text-ink">
            <span className="fl-num">{formatPhone(m.phone)}</span>
            <CopyButton value={m.phone} label="Mobile" />
          </dd>
          {m.state ? (
            <>
              <dt className="text-ink-muted">State</dt>
              <dd className="text-ink">{m.state}</dd>
            </>
          ) : null}
          {local ? (
            <>
              <dt className="text-ink-muted">Local time</dt>
              <dd className="text-ink" data-testid="details-local-time">
                {local}
              </dd>
            </>
          ) : null}
          {rep ? (
            <>
              <dt className="text-ink-muted">Owner</dt>
              <dd className="truncate text-ink">{rep.fullName || rep.email}</dd>
            </>
          ) : null}
        </dl>
      </PanelCard>

      <PanelCard title="Texting">
        <p className="callout text-ink">
          {m.optedOutAt
            ? 'Opted out. Texts are paused until they reply START.'
            : m.doNotContact
              ? `Marked do not contact${m.dncReason ? `: ${m.dncReason}` : ''}.`
              : m.archivedAt
                ? 'Archived. Restore the merchant to text them.'
                : 'Can be texted.'}
        </p>
      </PanelCard>

      {tagList.length > 0 ? (
        <PanelCard title="Tags">
          <div className="flex flex-wrap gap-1">
            {tagList.map((t) => (
              <TagChip key={t.id} name={t.name} color={t.color} />
            ))}
          </div>
        </PanelCard>
      ) : null}
    </>
  );

  const header = (
    <div className="flex items-center justify-between gap-2">
      <h2 className="headline text-ink">Details</h2>
      <button
        ref={closeButton}
        type="button"
        className="fl-icon-btn text-ink-muted hover:text-ink focus-visible:outline-2 focus-visible:outline-focus-ring"
        aria-label="Close details"
        onClick={onClose}
      >
        <X size={20} strokeWidth={1.75} aria-hidden />
      </button>
    </div>
  );

  const content = (
    <div className="flex flex-col gap-3" data-testid="details-panel">
      {header}
      {body}
    </div>
  );

  if (mode === 'column') {
    return (
      <aside
        aria-label="Details"
        className="w-80 shrink-0 overflow-y-auto border-l border-hairline bg-surface p-3"
      >
        {content}
      </aside>
    );
  }

  return (
    <div className="fixed inset-0 z-40" role="presentation">
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        className="absolute inset-0 size-full cursor-default bg-ink opacity-30"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Details"
        className={
          mode === 'overlay'
            ? 'absolute inset-y-0 right-0 w-80 overflow-y-auto border-l border-hairline bg-surface p-3 shadow-sheet'
            : 'absolute inset-x-0 bottom-0 max-h-[85dvh] overflow-y-auto rounded-t-lg bg-surface p-4 pb-[calc(env(safe-area-inset-bottom)+var(--space-4))] shadow-sheet'
        }
      >
        {content}
      </aside>
    </div>
  );
}
